import { Mesh, TransformNode } from "@babylonjs/core";

/**
 * Collapse a zone's static shell geometry into one mesh per
 * (material, collidable) group. A two-story mall built from MeshBuilder boxes
 * is thousands of individual draw calls; merging brings a zone down to a
 * handful. Call AFTER the static build and BEFORE any dynamic content
 * (streamed interiors build later, so they are excluded by construction).
 *
 * MergeMeshes bakes world matrices, so merged meshes stay at scene root
 * (do NOT re-parent under a rotated zone root). Skipped: invisible meshes
 * (trigger volumes), clickable content with an ActionManager, meshes matched
 * by `keep` (e.g. floors that floorMeshes() must keep referencing), and
 * single-mesh groups.
 */
export function mergeStaticChildren(
  root: TransformNode,
  keep?: (m: Mesh) => boolean
): void {
  const groups: Map<string, Mesh[]> = new Map();
  for (const child of root.getChildMeshes()) {
    // InstancedMesh (prop placements) must never merge: merging would clone
    // the geometry per placement and destroy the instancing batch.
    if (!(child instanceof Mesh)) {
      continue;
    }
    const mesh: Mesh = child;
    if (
      !mesh.isVisible ||
      mesh.actionManager !== null ||
      mesh.material === null ||
      mesh.getTotalVertices() === 0 ||
      (keep !== undefined && keep(mesh))
    ) {
      continue;
    }
    const key: string = `${mesh.material.uniqueId}|${mesh.checkCollisions ? 1 : 0}`;
    const list: Mesh[] = groups.get(key) ?? [];
    list.push(mesh);
    groups.set(key, list);
  }

  for (const [key, list] of groups) {
    if (list.length < 2) {
      for (const m of list) {
        m.freezeWorldMatrix();
      }
      continue;
    }
    const collidable: boolean = key.endsWith("|1");
    const merged: Mesh | null = Mesh.MergeMeshes(list, true, true, undefined, false, false);
    if (merged === null) {
      continue;
    }
    merged.checkCollisions = collidable;
    merged.isPickable = false;
    merged.freezeWorldMatrix();
  }
}
